"use client";

// CommandContextProvider centralises the data every command handler
// might need: routing, the current load/driver/customer the user is
// looking at, plus permission + feature-flag lookups. Handlers and
// scope filters get a single CommandContext object from useCommandContext().
import * as React from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { useUser, useOrganization } from "@clerk/nextjs";
import { useFeatureFlags } from "@/lib/hooks/use-feature-flags";
import { usePermissions } from "@/lib/hooks/use-permissions";
import type { Permission } from "@/lib/permissions";
import type { CommandContext, FeatureFlag } from "@/lib/commands/types";

const Ctx = React.createContext<CommandContext | null>(null);

// resolveSelection inspects the pathname + query string and pulls
// out the entity ID the user is currently looking at. We avoid
// per-route React contexts because (a) the URL is already canonical
// and (b) the command bar mounts at the app shell — it must work on
// every page without each page wiring it up.
function resolveSelection(pathname: string, searchParams: URLSearchParams) {
  const id = searchParams.get("id") ?? undefined;
  const sel: {
    loadId?: string;
    driverId?: string;
    customerId?: string;
    orderId?: string;
  } = {};
  if (pathname.startsWith("/loads") && id) sel.loadId = id;
  else if (pathname.startsWith("/fleet/drivers") && id) sel.driverId = id;
  else if (pathname.startsWith("/customers") && id) sel.customerId = id;
  else if (pathname.startsWith("/orders") && id) sel.orderId = id;
  return sel;
}

export function CommandContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { user } = useUser();
  const { organization } = useOrganization();
  const { can } = usePermissions();
  const { flags } = useFeatureFlags();

  const value = React.useMemo<CommandContext>(() => {
    const sel = resolveSelection(pathname ?? "", searchParams ?? new URLSearchParams());
    return {
      pathname: pathname ?? "",
      router: { push: (href: string) => router.push(href) },
      ...sel,
      userId: user?.id,
      orgId: organization?.id,
      has: (permission: Permission) => can(permission),
      hasFeature: (flag: FeatureFlag) => Boolean(flags[flag]),
    };
  }, [pathname, searchParams, router, user?.id, organization?.id, can, flags]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCommandContext(): CommandContext {
  const ctx = React.useContext(Ctx);
  if (!ctx) {
    throw new Error("useCommandContext must be used inside CommandContextProvider");
  }
  return ctx;
}
